export const INTEGRATIONS = {
  pageTitle: 'Integrations',
  callout:
    'Integration configuration is coming soon. Contact PolyAI for integration support',
  filterToggleButton: {
    activeFilters: (activeFilters: number) => `${activeFilters} active filters`,
    filterToggleText: 'Filter integrations',
  },
  emptyState: {
    title: 'No integrations match your search and filters',
    text: 'Try adjusting your spelling or selected filters',
  },
  filterOptions: {
    telephony: 'Telephony',
    crm: 'CRM',
    knowledgeBase: 'Knowledge Base',
    hospitality: 'Hospitality',
    healthcare: 'Healthcare',
    chat: 'Chat',
  },
  forms: {
    telephoneErrorMessage:
      'Please enter a valid phone number with country code (e.g. +441234567890)',
    formErrorMessage: 'Please fix the errors in the form before proceeding',
    stepConfigurationNotFoundMessage: 'Step configuration not found',
  },
  environmentTab: {
    connectionDetails: {
      connectedLabel: 'Connected',
      configurationTitle: 'Configuration',
      configurationDescription:
        'If this connection is used by other environments, your changes will also take effect there.',
    },
    connectForm: {
      useExistingConnectionLabel: 'Use existing connection',
      connectNewAccount: 'Connect new account',
      connectTo: (name: string) => `Connect to ${name}`,
    },
    metadataValues: {
      yes: 'Yes',
      no: 'No',
    },
  },
  connectionPanel: {
    disconnectModal: {
      titleDisable: 'Disable Environment',
      titleDisconnect: 'Disconnect Integration',
      liveEnvironmentWarning: {
        title: '⚠️ Warning: Live Environment',
        description:
          'Disconnecting from the live environment could affect active production traffic.',
      },
      sourceHubKbWarning: {
        title: '⚠️ SourceHub KB Enabled',
        descriptionShared:
          'This connection has SourceHub KB enabled. Disconnecting will trigger a resync of the knowledge base.',
        descriptionSole:
          'This connection has SourceHub KB enabled. Disconnecting will permanently delete the associated knowledge base source.',
      },
      sharedConnectionWarning: {
        title: 'Connection used by multiple environments',
        usedBy: (envs: string) => `This connection is also used by: ${envs}`,
        onlyDisabledFor: (env: string) =>
          `The connection will only be disabled for ${env} environment.`,
      },
      confirmDisable: (name: string, env: string) =>
        `Are you sure you want to disable ${name} for the ${env} environment?`,
      confirmDisconnect: (name: string, env: string) =>
        `Are you sure you want to disconnect ${name} from the ${env} environment?`,
      actions: {
        disable: 'Disable',
        disconnect: 'Disconnect',
        cancel: 'Cancel',
      },
    },
    toasts: {
      environmentDisabled: {
        title: 'Environment disabled',
        description: (name: string, env: string) =>
          `Removed ${name} from ${env} environment`,
      },
      failedToDisable: {
        title: 'Failed to disable environment',
        unknownError: 'An unexpected error occurred',
      },
      integrationDisconnected: {
        title: 'Integration disconnected',
        description: (name: string) => `${name} has been disconnected`,
      },
      failedToDisconnect: {
        title: 'Failed to disconnect integration',
        description: 'An error occurred while disconnecting. Please try again.',
      },
      noCopySource: {
        title: 'No configuration to copy',
        description: (env: string) => `No ${env} configuration found`,
      },
    },
  },
  mcpIntegrationCard: {
    functionsAvailable: (count: number) => `${count} functions available`,
  },
  mcpConfigurationPanel: {
    subtitleConnected: 'You can use these functions to build your agents.',
    subtitleDisconnected: (providerName: string) =>
      `Configure and authenticate the setup to use functions in ${providerName}`,
    refreshingFunctions: 'Refreshing MCP functions...',
    savingFunctionSelection: 'Saving function selection...',
  },
  mcpServerModal: {
    titleEdit: 'Edit MCP integration',
    titleAdd: 'Add new MCP integration',
    subtitle: 'Configure the MCP server connection settings',
    secretHelp:
      'Add a secret with this name in the Secrets page and grant access to this project',
  },
  mcpIntegrationsTab: {
    catalogError:
      'Failed to load MCP catalog. You can still add a custom MCP server.',
    customProviderDescription: 'Custom MCP integration',
    emptyState: {
      title: 'No MCP integrations found',
      text: 'Add an MCP server to discover and configure tools.',
    },
    toasts: {
      failedToConnect: 'Failed to connect MCP server',
      failedToUpdate: 'Failed to update MCP server',
      failedToSave: 'Failed to save MCP server',
      failedToDiscover: 'Failed to discover MCP tools',
      failedToRefresh: 'Failed to refresh MCP functions',
      failedToDiscoverFunctions: 'Failed to discover MCP functions',
      unknownError: 'Unknown error',
    },
  },
  mcpFunctionToggles: {
    emptyState: 'No functions available for this MCP server',
  },
  modals: {
    disconnect: {
      title: (integrationName: string) =>
        `Are you sure you want to disconnect ${integrationName}?`,
      description: (integrationName: string) =>
        `This action will remove all ${integrationName} data from your project.`,
      button: {
        cancel: 'Cancel',
        confirm: 'Disconnect',
      },
    },
    mcpDisconnect: {
      title: (integrationName: string) =>
        `Are you sure you want to disconnect ${integrationName}?`,
      description: (integrationName: string) =>
        `You will not be able to use functions provided by ${integrationName}.`,
      button: {
        cancel: 'Cancel',
        confirm: 'Disconnect',
      },
    },
    connectWarning: {
      title: (integrationName: string) =>
        `Are you sure you want to connect to ${integrationName}?`,
      description: (activeIntegrations: string, integrationCategory: string) =>
        `You are currently connected to ${activeIntegrations} other integrations. Please be aware that simultaneous ${integrationCategory} integrations are not recommended. Would you like to proceed?`,
      button: {
        cancel: 'Cancel',
        confirm: 'Proceed',
      },
    },
    restricted: {
      title: 'Integration Restricted',
      description: (integrationName: string) => (
        <>
          <strong>{integrationName}</strong> is not available on demo accounts.
        </>
      ),
      upgradeMessage:
        'Upgrade to an Enterprise plan or speak to the PolyAI team to gain access to this integration.',
      button: {
        close: 'Close',
        contact: 'Contact PolyAI Team',
      },
    },
    handoffWarning: {
      title: 'Update handoff configuration',
      content:
        'Please make sure to update your handoff configuration to ensure your integration is working correctly.',
      primaryButton: 'Take me there',
      secondaryButton: 'Dismiss',
    },
  },
};
